import axios, { AxiosHeaders, type AxiosError } from 'axios'

import {
  FalhaHttp,
  type DadosFalhaHttp,
  type ErroDeCampoRecebido,
  type TipoFalhaHttp,
} from './falha-http'
import type { MetodoHttp } from './http.tipos'

export type ContextoFalhaHttp = Readonly<{
  servico: string
  metodo?: MetodoHttp
  endpoint?: string
}>

const ehRegistro = (valor: unknown): valor is Record<string, unknown> => (
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)
)

const obterTexto = (valor: unknown): string | undefined => {
  if (typeof valor !== 'string') return undefined
  const texto = valor.trim()
  return texto.length > 0 ? texto : undefined
}

const ehHtml = (valor: string): boolean => /<!doctype\s+html|<html(?:\s|>)/i.test(valor)

const obterCodigo = (data: unknown): string | undefined => {
  if (!ehRegistro(data)) return undefined
  return obterTexto(data.code) ?? obterTexto(data.codigo)
}

const obterMensagemDeErroAninhado = (valor: unknown): string | undefined => {
  if (!ehRegistro(valor)) return undefined
  return obterTexto(valor.message) ?? obterTexto(valor.mensagem)
}

const obterMensagem = (data: unknown): string | undefined => {
  const textoDireto = obterTexto(data)
  if (textoDireto) return ehHtml(textoDireto) ? undefined : textoDireto
  if (!ehRegistro(data)) return undefined

  const erro = data.error ?? data.erro
  const mensagemErro = obterTexto(erro) ?? obterMensagemDeErroAninhado(erro)
  if (mensagemErro) return ehHtml(mensagemErro) ? undefined : mensagemErro

  const detalhe = data.detail
  const mensagemDetalhe = obterTexto(detalhe)
  if (mensagemDetalhe) return ehHtml(mensagemDetalhe) ? undefined : mensagemDetalhe
  if (Array.isArray(detalhe)) {
    const primeiroDetalhe = detalhe.find(ehRegistro)
    const mensagemPydantic = primeiroDetalhe ? obterTexto(primeiroDetalhe.msg) : undefined
    if (mensagemPydantic) return mensagemPydantic
  }

  const mensagem = obterTexto(data.description)
    ?? obterTexto(data.message)
    ?? obterTexto(data.mensagem)
  return mensagem && !ehHtml(mensagem) ? mensagem : undefined
}

const criarErroDeCampo = (
  campo: string,
  mensagemRecebida?: string,
  codigoRecebido?: string,
): ErroDeCampoRecebido => ({
  campo,
  ...(codigoRecebido ? { codigoRecebido } : {}),
  ...(mensagemRecebida ? { mensagemRecebida } : {}),
})

const obterErrosPydantic = (data: Record<string, unknown>): ErroDeCampoRecebido[] => {
  if (!Array.isArray(data.detail)) return []

  return data.detail.flatMap((detalhe) => {
    if (!ehRegistro(detalhe) || !Array.isArray(detalhe.loc)) return []
    const localizacao = detalhe.loc as unknown[]
    const campo = [...localizacao].reverse().find((parte): parte is string => typeof parte === 'string')
    if (!campo) return []
    return [criarErroDeCampo(campo, obterTexto(detalhe.msg), obterTexto(detalhe.type))]
  })
}

const obterPrimeiraMensagem = (valor: unknown): string | undefined => {
  if (Array.isArray(valor)) {
    return valor.map(obterTexto).find((texto) => texto !== undefined)
  }
  return obterTexto(valor)
}

const obterErrosMapeados = (data: Record<string, unknown>): ErroDeCampoRecebido[] => {
  if (!ehRegistro(data.errors)) return []
  return Object.entries(data.errors).flatMap(([campo, valor]) => {
    const mensagem = obterPrimeiraMensagem(valor)
    return mensagem ? [criarErroDeCampo(campo, mensagem)] : []
  })
}

const obterErrosDeCampo = (data: unknown): readonly ErroDeCampoRecebido[] | undefined => {
  if (!ehRegistro(data)) return undefined
  const erros = [...obterErrosPydantic(data), ...obterErrosMapeados(data)]
  return erros.length > 0 ? erros : undefined
}

const obterHeader = (headers: unknown, nome: string): string | undefined => {
  if (headers instanceof AxiosHeaders) return obterTexto(headers.get(nome))
  if (!ehRegistro(headers)) return undefined
  const chave = Object.keys(headers).find((item) => item.toLowerCase() === nome)
  return chave ? obterTexto(headers[chave]) : undefined
}

const obterRequestId = (erro: AxiosError): string | undefined => {
  if (!erro.response) return undefined
  const headers: unknown = erro.response.headers
  return obterHeader(headers, 'x-request-id')
    ?? obterHeader(headers, 'x-correlation-id')
}

export const obterDiagnosticoRespostaHttp = (
  erro: AxiosError,
): Pick<DadosFalhaHttp, 'codigoRecebido' | 'mensagemRecebida' | 'requestId'> => {
  const data: unknown = erro.response?.data
  const codigoRecebido = obterCodigo(data)
  const mensagemRecebida = obterMensagem(data)
  const requestId = obterRequestId(erro)

  return {
    ...(codigoRecebido ? { codigoRecebido } : {}),
    ...(mensagemRecebida ? { mensagemRecebida } : {}),
    ...(requestId ? { requestId } : {}),
  }
}

const obterTipoFalha = (erro: unknown): TipoFalhaHttp => {
  if (axios.isCancel(erro)) return 'cancelamento'
  if (!axios.isAxiosError(erro)) return 'resposta_invalida'
  if (erro.code === 'ECONNABORTED' || erro.code === 'ETIMEDOUT') return 'timeout'
  return erro.response ? 'http' : 'rede'
}

export const normalizarFalhaHttp = (
  erro: unknown,
  contexto: ContextoFalhaHttp,
): FalhaHttp => {
  const erroAxios = axios.isAxiosError(erro) ? erro : undefined
  const data: unknown = erroAxios?.response?.data
  const status = erroAxios?.response?.status
  const errosDeCampo = obterErrosDeCampo(data)

  const dados: DadosFalhaHttp = {
    tipo: obterTipoFalha(erro),
    servico: contexto.servico,
    causa: erro,
    ...(status === undefined ? {} : { status }),
    ...(erroAxios ? obterDiagnosticoRespostaHttp(erroAxios) : {}),
    ...(errosDeCampo ? { errosDeCampo } : {}),
    ...(contexto.metodo ? { metodo: contexto.metodo } : {}),
    ...(contexto.endpoint ? { endpoint: contexto.endpoint } : {}),
  }

  return new FalhaHttp(dados)
}
